import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import AdminLayout from "../../layouts/AdminLayout";
import Button from "../../components/ui/Button";
import FormField from "../../components/ui/FormField";
import TextInput from "../../components/ui/TextInput";
import Textarea from "../../components/ui/Textarea";
import SelectDropdown from "../../components/ui/SelectDropdown";
import {
  Megaphone,
  Send,
  Smartphone,
  Bell
} from "lucide-react";
import { getSocieties } from "../../services/societyService";
import styles from "./SendNotifications.module.css";

const AUDIENCE_OPTIONS = [
  { id: "all", label: "All Members" },
  { id: "owners", label: "Owners Only" },
  { id: "tenants", label: "Tenants Only" },
  { id: "staff", label: "Staff Only" },
];

const PRIORITY_OPTIONS = [
  { id: "normal", label: "General Notice" },
  { id: "urgent", label: "Urgent / Alert" },
];

const SendNotifications = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedSocietyId, setSelectedSocietyId] = useState("");
  const [societies, setSocieties] = useState([]);
  const [targetAudience, setTargetAudience] = useState("all");
  const [priority, setPriority] = useState("normal");

  useEffect(() => {
    loadSocieties();
  }, []);

  const loadSocieties = async () => {
    try {
      const data = await getSocieties();
      const list = Array.isArray(data) ? data : data?.societies || [];
      setSocieties(list);
      if (list.length > 0 && !selectedSocietyId) {
        setSelectedSocietyId(String(list[0].society_id || list[0].id));
      }
    } catch (err) {
      console.error("Error loading societies:", err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Please provide both a notification title and message body.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/community/broadcast", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          society_id: selectedSocietyId ? Number(selectedSocietyId) : undefined,
          title: title.trim(),
          body: body.trim(),
          audience: targetAudience,
          priority,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Notification broadcast sent successfully!");
        setTitle("");
        setBody("");
      } else {
        toast.error(data.message || "Failed to broadcast notification.");
      }
    } catch (error) {
      console.error("Broadcast notification error:", error);
      toast.error("An error occurred while broadcasting notification.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleArea}>
            <div className={styles.iconBadge}>
              <Megaphone size={22} />
            </div>
            <div>
              <h1 className={styles.pageTitle}>Notice & Broadcast Center</h1>
              <p className={styles.pageSubtitle}>
                Compose and dispatch instant push notifications to society residents.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.grid}>
          {/* Form Composer */}
          <div className={styles.composerCard}>
            <div className={styles.cardHeading}>
              <span>Compose Broadcast</span>
              <span className={styles.optionalBadge}>Live Push Delivery</span>
            </div>

            <form onSubmit={handleSend} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Target Property */}
              <FormField label="Target Property" hint="Leave as All Properties to broadcast everywhere">
                <SelectDropdown
                  value={selectedSocietyId}
                  onChange={(e) => setSelectedSocietyId(e.target.value)}
                  placeholder="All Managed Properties (Broadcast Everywhere)"
                  options={[
                    { label: "All Managed Properties (Broadcast Everywhere)", value: "" },
                    ...societies.map((soc) => {
                      const id = String(soc.society_id || soc.id);
                      const name = soc.name || soc.society_name || `Property #${id}`;
                      return { value: id, label: name };
                    })
                  ]}
                />
              </FormField>

              {/* Target Audience */}
              <FormField label="Target Audience">
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      variant={targetAudience === opt.id ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setTargetAudience(opt.id)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </FormField>

              {/* Priority */}
              <FormField label="Priority Level">
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      variant={priority === opt.id ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setPriority(opt.id)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </FormField>

              {/* Title */}
              <FormField label="Notification Title" required>
                <TextInput
                  maxLength={60}
                  showCharCount
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Scheduled Maintenance Tomorrow"
                  required
                />
              </FormField>

              {/* Message Body */}
              <FormField label="Message Body" required>
                <Textarea
                  maxLength={300}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter the complete notification message..."
                  rows={4}
                  required
                />
              </FormField>

              <div className={styles.cardFooter}>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setTitle("");
                    setBody("");
                  }}
                  disabled={loading || (!title && !body)}
                >
                  Clear
                </Button>
                <Button variant="primary" type="submit" loading={loading}>
                  <Send size={15} style={{ marginRight: 6 }} /> Send Broadcast
                </Button>
              </div>
            </form>
          </div>

          {/* Live Mobile Preview */}
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              <Smartphone size={18} color="var(--color-dark-bg)" />
              <span>Real-Time Mobile Push Preview</span>
            </div>

            <div className={styles.phoneDevice}>
              <div className={styles.phoneNotch} />
              <div className={styles.phoneScreen}>
                <div className={styles.phoneStatusBar}>
                  <span>9:41</span>
                  <span>5G • 100%</span>
                </div>

                <div className={styles.notificationCard}>
                  <div className={styles.notifHeader}>
                    <div className={styles.notifAppIcon}>
                      <Bell size={11} />
                    </div>
                    <span className={styles.notifAppName}>myAsmitA Portal</span>
                    <span className={styles.notifTime}>now</span>
                  </div>
                  <h4 className={styles.notifTitle}>
                    {title.trim() || "Notification Title"}
                  </h4>
                  <p className={styles.notifBody}>
                    {body.trim() ||
                      "This preview reflects how residents will receive and see your broadcast on their lock screen."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SendNotifications;
